import React, { FC, Fragment, useEffect, useState } from 'react';
import { Button, Checkbox, Form } from 'antd';
import { useIntl } from 'react-intl';
import {
  CustomInputText,
  CustomInputTextArea,
  CustomOnlyNumber,
  CustomSelector,
} from 'src/components/forms/pin/CustomInputText';
import { ComboData, IAddressData } from 'src/shared/models';
import {
  customRegexName,
  phoneRegex,
  useWindowSize,
  zipCodeFormatFrance,
  directionCharacters,
} from 'utils';
import { IAddressFormValues } from 'src/components/forms/interfaces';

interface Props {
  address?: IAddressData;
  isEditForm?: boolean;
  isFirstAddress?: boolean;
  onCreateAddress?: (address: IAddressData) => void;
  onUpdateAddress?: (address: IAddressData) => void;
  regionList: ComboData[];
  isLoading?: boolean;
}

const AddressFormFr: FC<Props> = ({
  address,
  isEditForm,
  isFirstAddress,
  onCreateAddress,
  onUpdateAddress,
  regionList,
  isLoading = false,
}): JSX.Element => {
  const [form] = Form.useForm();
  const { isMD } = useWindowSize();
  const { formatMessage } = useIntl();
  const [addressData, setAddressData] = useState(address);

  const formValues: Partial<IAddressData> = {
    name: address?.name || '',
    surname: address?.surname || '',
    address: address?.address.includes('#')
      ? address.address.substring(0, address.address.indexOf('#'))
      : address?.address || '',
    addressOptionalInfo: address?.address.includes('#')
      ? address?.address.substring(address.address.indexOf('#') + 1)
      : '',
    phone: address?.phone || '',
    zipCode: address?.zipCode || '',
    city: address?.city || '',
    regionName: address?.regionName || '',
    deliveryInstructions: address?.deliveryInstructions || '',
  };

  let formFields: IAddressFormValues[] = [
    {
      type: 'text',
      name: 'name',
      placeholder: 'form.field.name',
      handleChange: 'name',
      initialValue: formValues.name,
      rules: {
        pattern: customRegexName,
        message: formatMessage(
          { id: 'form.field.error-only-letters' },
          { name: formatMessage({ id: 'form.field.name' }) },
        ),
      },
      autocomplete: 'given-name',
      length: 25,
      value: addressData?.name,
    },
    {
      type: 'text',
      name: 'surname',
      placeholder: 'form.field.last-name',
      handleChange: 'surname',
      initialValue: formValues.surname,
      rules: {
        pattern: customRegexName,
        message: formatMessage(
          { id: 'form.field.error-only-letters' },
          { name: formatMessage({ id: 'form.field.last-name' }) },
        ),
      },
      autocomplete: 'family-name',
      length: 50,
      value: addressData?.surname,
    },
    {
      type: 'text',
      name: 'address',
      placeholder: 'form.field.address',
      handleChange: 'address',
      initialValue: formValues.address,
      rules: {
        pattern: directionCharacters,
        message: formatMessage({ id: 'form.field.error-restricted-error' }),
      },
      autocomplete: 'street-address',
      length: 40,
      value: addressData?.address,
    },
    {
      type: 'optionalText',
      name: 'addressOptionalInfo',
      placeholder: 'form.field.addressOptionalInfo',
      handleChange: 'addressOptionalInfo',
      initialValue: formValues.addressOptionalInfo,
      rules: {
        pattern: directionCharacters,
        message: formatMessage({ id: 'form.field.error-restricted-error' }),
      },
      autocomplete: 'address-line1',
      length: 20,
      value: addressData?.addressOptionalInfo,
    },
    {
      type: 'number',
      name: 'phone',
      placeholder: 'form.field.telephone',
      handleChange: 'phone',
      initialValue: formValues.phone,
      rules: {
        pattern: phoneRegex.FR,
        message: formatMessage({
          id: 'form.field.phone-error-number',
        }),
      },
      autocomplete: 'tel',
      length: 10,
      value: addressData?.phone,
    },
    {
      type: 'number',
      name: 'zipCode',
      placeholder: 'form.field.zipCode',
      handleChange: 'zipCode',
      initialValue: formValues.zipCode,
      rules: {
        pattern: zipCodeFormatFrance,
        message: formatMessage(
          { id: 'form.field.zipCode-error' },
          { name: formatMessage({ id: 'form.field.zip-code' }) },
        ),
      },
      autocomplete: 'postal-code',
      length: 5,
      value: addressData?.zipCode,
    },
    {
      type: 'text',
      name: 'city',
      placeholder: 'form.field.city',
      handleChange: 'city',
      initialValue: formValues.city,
      rules: {
        pattern: customRegexName,
        message: formatMessage(
          { id: 'form.field.error-only-letters' },
          { name: formatMessage({ id: 'form.field.city' }) },
        ),
      },
      autocomplete: 'address-level2',
      length: 50,
      value: addressData?.city,
    },
    {
      type: 'regionSelector',
      name: 'regionName',
      placeholder: 'form.field.state',
      handleChange: 'regionName',
      initialValue: formValues.regionName,
      rules: {
        pattern: customRegexName,
        message: formatMessage(
          { id: 'form.field.error-only-letters' },
          { name: formatMessage({ id: 'form.field.state' }) },
        ),
      },
    },
    {
      type: 'optionalTextArea',
      name: 'deliveryInstructions',
      placeholder: 'form.field.deliveryInstructions',
      handleChange: 'deliveryInstructions',
      initialValue: '',
      rules: {
        pattern: directionCharacters,
        message: formatMessage({ id: 'form.field.error-restricted-error' }),
      },
      length: 250,
      value: addressData?.deliveryInstructions,
    },
  ];

  useEffect(() => {
    checkCanContinue();
  });

  useEffect(() => {
    form.resetFields();
    if (!addressData) return;
    form.setFieldsValue(formValues);
    setAddressData((data) => {
      return {
        ...data,
        address: formValues.address,
        addressOptionalInfo: formValues.addressOptionalInfo,
      };
    });
  }, []);

  useEffect(() => {
    if (!isFirstAddress) return;

    setAddressData((currentAddress) => {
      return {
        ...currentAddress,
        defaultAddress: true,
      };
    });
  }, []);

  const isDefaultAddressChecked = (): boolean => {
    if ((!isEditForm && isFirstAddress) || address?.defaultAddress) {
      return true;
    }
    return false;
  };

  const isDefaultAddressDisabled = () => {
    const editingDefaultAddress = Boolean(isEditForm && address.defaultAddress);

    if (isFirstAddress || editingDefaultAddress) return true;
    return false;
  };

  const getCompleteAdressData = () => {
    return addressData.addressOptionalInfo
      ? `${addressData.address}#${addressData.addressOptionalInfo}`
      : addressData.address;
  };

  const handleSubmit = () => {
    let completeAddressData = getCompleteAdressData();
    let newAddress = { ...addressData, address: completeAddressData };
    isEditForm ? onUpdateAddress(newAddress) : onCreateAddress(newAddress);
    form.resetFields();
  };

  const checkAddressForm = (addressData: IAddressData): boolean => {
    return Boolean(
      addressData?.name?.match(customRegexName) &&
        addressData?.surname?.match(customRegexName) &&
        addressData?.address &&
        addressData?.phone?.match(phoneRegex.FR) &&
        addressData?.zipCode?.match(zipCodeFormatFrance) &&
        addressData?.city?.match(customRegexName) &&
        addressData?.regionName,
    );
  };

  const checkCanContinue = () => {
    const isDataCorrect = checkAddressForm(addressData);
    if (addressData?.canContinue == isDataCorrect) return;
    setAddressData((data) => {
      return {
        ...data,
        canContinue: isDataCorrect,
      };
    });
  };

  const handleChange = (field: string) => (event: any) => {
    if (event.persist) event.persist();
    setAddressData((data) => {
      const newData = { ...data };
      let value = event?.target?.value;

      switch (field) {
        case 'phone':
        case 'zipCode':
        case 'regionName':
          value = event;
          break;
        case 'defaultAddress':
          value = event.target.checked;
          break;
        default:
          value = event.target.value;
          break;
      }

      return { ...newData, [field]: value };
    });
  };

  const renderTitle = (
    <>
      {isMD && (
        <h4 className="address__form-title">
          {isEditForm
            ? formatMessage({ id: 'form.address.edit-title' })
            : formatMessage({ id: 'form.address.new-title' })}
        </h4>
      )}
      {!isMD && !isEditForm && (
        <h4 className="address__form-title">
          {formatMessage({ id: 'form.address.new-title' })}
        </h4>
      )}
    </>
  );

  const renderFields = formFields.map((field) => {
    let component: JSX.Element;
    switch (field.type) {
      case 'text':
        component = CustomInputText(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length,
          true,
          [field.rules],
          field.value || null,
          field.autocomplete,
        );
        break;
      case 'optionalText':
        component = CustomInputText(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length,
          false,
          [field.rules],
          field.value || null,
          field.autocomplete,
        );
        break;
      case 'optionalTextArea':
        component = CustomInputTextArea(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length ?? 250,
          false,
          [field.rules],
          field.value || null,
          field.autocomplete,
        );
        break;
      case 'number':
        component = CustomOnlyNumber(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length ?? 40,
          true,
          [field.rules],
          field.value || '',
          field.autocomplete,
        );
        break;
      case 'regionSelector':
        component = CustomSelector(
          form,
          field.name,
          regionList,
          field.placeholder,
          'value',
          handleChange(field.name),
          false,
          'description',
        );
        break;
      default:
        return;
    }

    return <Fragment key={field.name}>{component}</Fragment>;
  });

  return (
    <>
      {renderTitle}
      <Form
        name="addressForm"
        form={form}
        className="address__form"
        onFinish={handleSubmit}>
        {renderFields}
        {!isDefaultAddressDisabled() && (
          <Form.Item
            name="defaultAddress"
            valuePropName="checked"
            initialValue={isDefaultAddressChecked()}>
            <Checkbox
              className="address__form__button-wrapper"
              defaultChecked={isDefaultAddressChecked()}
              onChange={handleChange('defaultAddress')}
              disabled={isDefaultAddressDisabled()}>
              {formatMessage({ id: 'form.field.address.preferred' })}
            </Checkbox>
          </Form.Item>
        )}
        <Form.Item shouldUpdate className="address__form__button-wrapper">
          {() => (
            <Button
              htmlType="submit"
              className="address__form__button-submit btn-primary btn-primary--black"
              disabled={!addressData?.canContinue || isLoading}>
              {formatMessage({ id: 'form.address.add-new' })}
              {isMD && (
                <i className="icon icon--arrow-normal-right-white button-icon" />
              )}
            </Button>
          )}
        </Form.Item>
      </Form>
    </>
  );
};

export default AddressFormFr;
